import { useGraphQLQuery } from '@opengov/bnp-core';
import { BNP_COMMON_SERVICE_URL } from '../../../../../config/config';
import { graphql } from '../../../../../gql-generated/bnp-common-service';
import { IEditorMessages } from '../../../../../types/storyEditorTypes';
import { useToast } from '../../../../components/Toast/context/useToast.context';
import { bnpAiAdminQueryKeys } from './bnpAiAdminQueryKeys';

const GetBnpAiToggleStateQuery = graphql(`
  query GetBnpAiToggleState($entityId: String!) {
    aiToggleState(where: { entityId: $entityId }) {
      aiEnabled
    }
  }
`);

export function useFetchBnpAiToggleState({
  entityUuid,
  messages,
  enabled,
  isAiToggleFeatureEnabled
}: {
  entityUuid: string;
  messages: IEditorMessages;
  enabled: boolean;
  isAiToggleFeatureEnabled: boolean;
}) {
  const { toggleToast } = useToast();

  return useGraphQLQuery({
    queryKey: bnpAiAdminQueryKeys.aiToggleState(entityUuid),
    variables: { entityId: entityUuid },
    document: GetBnpAiToggleStateQuery,
    url: BNP_COMMON_SERVICE_URL,
    options: {
      enabled: enabled,
      initialData: () => {
        /**
         * If the AI toggle feature flag is disabled,
         * we will initialize the toggle state to true to match the old behavior.
         * In other words, the non-existance of this check is the
         * same as a true value for this check.
         *
         * It's important to note that the AI functionality
         * still depends on other checks, for example the
         * LD flag that enables/disables the feature.
         * Those checks are still in place.
         *
         * Remove this initialData section once this flag is deprecated.
         */
        if (!isAiToggleFeatureEnabled) {
          return { aiToggleState: { aiEnabled: true } };
        }
      },
      select: (data) => data.aiToggleState.aiEnabled ?? false,
      onError: (error) => {
        console.error(error);
        toggleToast({
          title: messages.somethingWentWrongError,
          body: messages.unableToFetchAiAdminSettings,
          severity: 'error'
        });
      }
    }
  });
}
